import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tradingService, type AlgorithmConfig } from '../../services/trading';
import { accountsService } from '../../services/accounts';
import type { Account } from '@trading-app/shared';

type Mode = 'ERIC' | 'ANNIE';

export function TradeConfiguration() {
  const queryClient = useQueryClient();

  // Form state
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedMode, setSelectedMode] = useState<Mode>('ERIC');
  const [units, setUnits] = useState<string>('');
  const [newWebhookSecret, setNewWebhookSecret] = useState<string>('');

  // Fetch user accounts
  const { data: accounts = [], isLoading: isLoadingAccounts, error: accountsError } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsService.getAccounts,
  });

  // Fetch algorithm configurations for selected account
  const { data: algorithmConfigs = [], isLoading: isLoadingConfigs } = useQuery({
    queryKey: ['algorithmConfigs', selectedAccount],
    queryFn: () => tradingService.getAlgorithmConfigs(selectedAccount),
    enabled: !!selectedAccount,
  });

  // Find active config for selected account + mode. Eric and Annie can coexist
  // per (account, mode) — so a user with an active Eric config can still reach
  // the create form for Annie (and vice versa). Configs predating the `mode`
  // field are treated as Eric (legacy default, matches API back-compat at
  // apps/api/src/routes/trading.ts:1027-1064).
  const activeConfig = algorithmConfigs.find(
    (config: AlgorithmConfig) =>
      config.isActive && (config.mode ?? 'ERIC') === selectedMode,
  );

  // Track accounts loading for error handling
  useEffect(() => {
    if (accountsError) {
      console.error('Error loading accounts:', accountsError);
    }
  }, [accounts, accountsError]);

  // Reset state when account or mode changes
  useEffect(() => {
    setNewWebhookSecret('');
    setUnits('');
  }, [selectedAccount, selectedMode]);

  // Configure webhook mutation
  const configureWebhookMutation = useMutation({
    mutationFn: ({
      accountId,
      algorithmType,
      units,
      mode,
    }: {
      accountId: string;
      algorithmType: string;
      units: number;
      mode: Mode;
    }) => tradingService.configureAlgorithm(accountId, algorithmType, units, mode),
    onSuccess: (data) => {
      // Refresh configs for both the specific account and all configs
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs', selectedAccount] });
      // The Mode toggle reads this; new Annie config can unlock the gate.
      // Key must match ModeToggle.tsx:29 (['mode-readiness'], hyphenated).
      queryClient.invalidateQueries({ queryKey: ['mode-readiness'] });

      // Store and show the webhook secret
      if (data.webhookSecret) {
        setNewWebhookSecret(data.webhookSecret);
      }

      console.log('Webhook connection configured successfully!');
    },
    onError: (error: any) => {
      console.error('Webhook configuration error:', error);
      // api-client's handleResponse throws `new Error(errorData.error ||
      // errorData.message)`, so for the 409 annie_handler_not_ready case
      // (apps/api/src/routes/trading.ts:1078-1083) error.message is the
      // machine code, not the friendly text. Map known codes to their
      // human-readable explanations here.
      const raw = error?.message ?? '';
      const msg =
        raw === 'annie_handler_not_ready'
          ? 'Annie mode config creation is currently disabled. The operator must flip ANNIE_HANDLER_READY=true after verifying the deployed handler.'
          : raw || 'Failed to create webhook connection';
      alert(msg);
    },
  });

  const handleAccountChange = (accountId: string) => {
    console.log('Selected account ID:', accountId);
    setSelectedAccount(accountId);
  };

  const handleCreateWebhook = () => {
    if (!selectedAccount) {
      alert('Please select an account');
      return;
    }
    if (!units || parseFloat(units) <= 0) {
      alert('Please enter a valid number of shares');
      return;
    }

    configureWebhookMutation.mutate({
      accountId: selectedAccount,
      algorithmType: 'MFI', // Keep MFI as the type for backend compatibility
      units: parseFloat(units),
      mode: selectedMode,
    });
  };

  const webhookUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/webhooks/tradingview`;

  return (
    <div className="space-y-6">
      {/* Account Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Account
        </label>
        <select
          value={selectedAccount}
          onChange={(e) => handleAccountChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isLoadingAccounts}
        >
          <option value="">Select an account...</option>
          {accounts.map((account: Account) => (
            <option key={account.id} value={account.id}>
              {account.accountName} - {account.brokerageName} ({account.accountNumber})
            </option>
          ))}
        </select>
      </div>

      {/* Mode Selection — Eric and Annie can coexist per (account, mode), so
          this picker drives both which config the UI inspects above AND which
          mode the create mutation tags the new config with. */}
      {selectedAccount && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mode
          </label>
          <div
            role="radiogroup"
            aria-label="Algorithm mode"
            className="grid grid-cols-2 gap-2"
          >
            {(['ERIC', 'ANNIE'] as const).map((mode) => {
              const isSelected = selectedMode === mode;
              const label = mode === 'ERIC' ? 'Eric' : 'Annie';
              const subtitle =
                mode === 'ERIC' ? 'Disciplined edge.' : 'Sniper-grade signals.';
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelectedMode(mode)}
                  className={`text-left px-4 py-3 rounded-md border transition-colors ${
                    isSelected
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-blue-400'
                  }`}
                >
                  <div className="text-sm font-semibold">{label}</div>
                  <div
                    className={`text-xs ${
                      isSelected
                        ? 'text-blue-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {subtitle}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading state */}
      {selectedAccount && isLoadingConfigs && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Render priority: freshly-created secret > active config > create form.
          The newWebhookSecret branch sits FIRST so the post-create refetch
          (which makes activeConfig truthy and would otherwise unmount this
          branch) can't hide the plaintext secret before the user copies it. */}
      {selectedAccount && !isLoadingConfigs && (
        <>
          {newWebhookSecret ? (
            /* Newly created secret — sticky until the user clicks "Close". */
            <div className="space-y-3">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                  ✅ {selectedMode === 'ANNIE' ? 'Annie' : 'Eric'} webhook connection created
                </div>
                <div className="text-xs text-green-700 dark:text-green-300">
                  Your webhook is now active and listening for TradingView signals.
                  {selectedMode === 'ANNIE'
                    ? ' Send at least one alert from TradingView to unlock the Annie mode toggle in Settings.'
                    : ''}
                </div>
              </div>

              {/* Webhook URL */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  🔗 Webhook URL for TradingView
                </div>
                <code className="block bg-blue-100 dark:bg-blue-900 p-2 rounded text-xs break-all text-blue-900 dark:text-blue-100">
                  {webhookUrl}
                </code>
              </div>

              {/* New Webhook Secret */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-md p-3">
                <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  🔑 Your Webhook Secret
                </div>
                <div className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                  Save this secret securely — you'll need it for your TradingView alerts:
                </div>
                <code className="block bg-yellow-100 dark:bg-yellow-900 p-2 rounded text-xs break-all text-yellow-900 dark:text-yellow-100 select-all">
                  {newWebhookSecret}
                </code>
                <div className="text-xs text-yellow-700 dark:text-yellow-400 mt-2 font-semibold">
                  ⚠️ This is the only time the full secret will be shown. Copy it into your password manager and your TradingView alert before clicking Close.
                </div>
              </div>

              {/* TradingView Alert Message Format */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                  📝 TradingView Alert Message Format
                </div>
                <code className="block bg-gray-100 dark:bg-gray-900 p-2 rounded text-xs break-all font-mono text-gray-900 dark:text-gray-100 select-all">
{`{
  "secret": "${newWebhookSecret}",
  "action": "{{strategy.order.action}}",
  "ticker": "{{ticker}}",
  "note": "{{strategy.order.comment}}"
}`}
                </code>
              </div>

              {/* Dismiss — only way the secret display goes away. */}
              <button
                type="button"
                onClick={() => setNewWebhookSecret('')}
                className="w-full py-3 px-4 rounded-md font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                I've saved my secret — close
              </button>
            </div>
          ) : activeConfig ? (
            /* Active Config Exists - Show Details */
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                  ✅ {selectedMode === 'ANNIE' ? 'Annie' : 'Eric'} webhook connection active
                </div>
                <div className="text-xs text-green-700 dark:text-green-300">
                  This account has an active {selectedMode === 'ANNIE' ? 'Annie' : 'Eric'} configuration. Trades will be placed automatically when TradingView signals are received for this mode.
                </div>
              </div>

              {/* Webhook URL */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  🔗 Webhook URL for TradingView
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                  Use this URL in your TradingView alert webhook settings:
                </div>
                <code className="block bg-blue-100 dark:bg-blue-900 p-2 rounded text-xs break-all text-blue-900 dark:text-blue-100">
                  {webhookUrl}
                </code>
              </div>

              {/* Webhook Secret */}
              {activeConfig.webhookSecret && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                    🔑 Your Webhook Secret
                  </div>
                  <div className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                    Include this secret in your TradingView alert message:
                  </div>
                  <code className="block bg-yellow-100 dark:bg-yellow-900 p-2 rounded text-xs break-all text-yellow-900 dark:text-yellow-100">
                    {activeConfig.webhookSecret}
                  </code>
                </div>
              )}

              {/* TradingView Alert Message Format */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                  📝 TradingView Alert Message Format
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  Use this exact format in your TradingView alert message:
                </div>
                <code className="block bg-gray-100 dark:bg-gray-900 p-2 rounded text-xs break-all font-mono text-gray-900 dark:text-gray-100">
{`{
  "secret": "${activeConfig.webhookSecret || 'YOUR_SECRET'}",
  "action": "{{strategy.order.action}}",
  "ticker": "{{ticker}}",
  "note": "{{strategy.order.comment}}"
}`}
                </code>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                To create a new webhook connection for this account, delete the existing one in the table below first.
              </div>
            </div>
          ) : (
            /* No Active Config - Show Create Form */
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                  No {selectedMode === 'ANNIE' ? 'Annie' : 'Eric'} webhook connection
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Create a {selectedMode === 'ANNIE' ? 'Annie' : 'Eric'} webhook connection to enable automated trading from TradingView alerts in this mode.
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shares per Signal
                </label>
                <input
                  type="number"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                  placeholder="Number of shares to trade per webhook signal"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  step="1"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This will be the fixed amount traded for each BUY/SELL signal from TradingView.
                </p>
              </div>

              <button
                onClick={handleCreateWebhook}
                disabled={configureWebhookMutation.isPending || !units || parseFloat(units) <= 0}
                className={`w-full py-3 px-4 rounded-md font-medium transition-colors ${
                  units && parseFloat(units) > 0
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                {configureWebhookMutation.isPending ? 'Creating...' : 'Create Webhook Connection'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
